import type { ReactNode } from 'react';

interface DecimalAlignedMoneyProps {
  value: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  leading?: ReactNode;
}

export function DecimalAlignedMoney({
  value,
  minimumFractionDigits = 2,
  maximumFractionDigits = 6,
  leading,
}: DecimalAlignedMoneyProps) {
  if (value.trim() === '') {
    return <span aria-label="No amount" className="flex h-full w-full items-center justify-end px-3 tabular-nums">—</span>;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return <span>—</span>;

  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).formatToParts(parsed);
  const decimalIndex = parts.findIndex((part) => part.type === 'decimal');
  const whole = parts.slice(0, decimalIndex).map((part) => part.value).join('');
  const decimal = decimalIndex >= 0 ? parts[decimalIndex].value : '.';
  const fraction = parts.find((part) => part.type === 'fraction')?.value ?? '';
  const accessibleValue = parts.map((part) => part.value).join('');

  return (
    <span
      aria-label={accessibleValue}
      className="inline-grid h-full w-full items-stretch whitespace-nowrap tabular-nums"
      style={{
        gridTemplateColumns: leading
          ? `calc(14px + 0.75rem) 0.25rem minmax(0, 1fr) auto calc(${maximumFractionDigits}ch + 0.75rem)`
          : `minmax(0, 1fr) auto calc(${maximumFractionDigits}ch + 0.75rem)`,
        minWidth: leading ? '24ch' : '20ch',
      }}
    >
      {leading && (
        <>
          <span aria-hidden="true" className="bg-foreground/[0.025] flex h-full items-center justify-center pl-3">{leading}</span>
          <span aria-hidden="true" className="bg-foreground/[0.025]" />
        </>
      )}
      <span
        data-decimal-side="whole"
        className={`bg-foreground/[0.025] flex h-full w-full items-center justify-end ${leading ? '' : 'pl-3'}`}
      >
        {whole}
      </span>
      <span
        data-decimal-side="fraction"
        className="bg-foreground/[0.06] border-foreground/15 flex h-full items-center border-l"
      >
        {decimal}
      </span>
      <span
        data-decimal-side="fraction"
        className="bg-foreground/[0.06] flex h-full w-full items-center justify-start pr-3"
      >
        {fraction}
      </span>
    </span>
  );
}
